import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '@utils/axios';
import { apiInternalPriceRulePath } from 'routes';
import SearchDropdown from './SearchDropdown';

const priceListInput = document.querySelector('[name*="proposal[price_list_id]"');
const resyncButton = document.querySelector('[data-js-sync-all-prices]');

function MaterialsServicesSelector({
  proposalProposedCorrection,
  proposedCorrection: initialProposedCorrection,
  priceRule: initialPriceRule,
  correctionName,
  priceRuleName,
  objectName,
  priceListId: initialPriceListId
}) {
  const [proposedCorrection, setProposedCorrection] = useState(initialProposedCorrection || { id: '', price: 0 });
  const [priceListId, setPriceListId] = useState(priceListInput?.value ?? initialPriceListId ?? '');
  const [priceRule, setPriceRule] = useState(initialPriceRule);
  const [price, setPrice] = useState(proposalProposedCorrection.proposedCorrectionPrice);
  const [firstTimeRender, setFirstTimeRender] = useState(true);
  const input = useRef();
  const [resyncButtonClicked, setResyncButtonClicked] = useState(false);
  const unitCostInput = document.querySelector(`[name*="${objectName}[unit_cost]"]`);
  const taxableCheckbox = document.querySelector(`input.taxable-checkbox[name*="${objectName}[taxable]"]`);

  useEffect(() => {
    const handlePriceListChange = (event) => setPriceListId(event.target.value);
    const handleResyncClick = () => setResyncButtonClicked(true);

    priceListInput?.addEventListener('change', handlePriceListChange);
    resyncButton?.addEventListener('click', handleResyncClick);

    return () => {
      priceListInput?.removeEventListener('change', handlePriceListChange);
      resyncButton?.removeEventListener('click', handleResyncClick);
    };
  }, []);

  const isProposalProposedCorrectionChanged = useMemo(
    () => proposalProposedCorrection.proposedCorrectionId?.toString() !== proposedCorrection.id?.toString(),
    [proposedCorrection]
  );

  const isPriceListChanged = useMemo(() => priceListId.toString() !== initialPriceListId?.toString(), [priceListId]);

  useEffect(() => {
    if (firstTimeRender) {
      setFirstTimeRender(false);
      return;
    }

    if (!priceListId || !proposedCorrection?.id) {
      setPriceRule({});
      setPrice(0);
      return;
    }

    if (!isProposalProposedCorrectionChanged && !isPriceListChanged && !resyncButtonClicked) {
      setPriceRule(initialPriceRule);
      setPrice(proposalProposedCorrection.proposedCorrectionPrice);
      return;
    }

    if (proposedCorrection.description_other) {
      document.querySelector(`[name*="${objectName}[description]"]`).value = proposedCorrection.description_other;
    }

    if (unitCostInput && proposedCorrection.unitCost) {
      unitCostInput.value = proposedCorrection.unitCost;
    }

    if (taxableCheckbox && proposedCorrection.taxable != null) {
      taxableCheckbox.checked = proposedCorrection.taxable;
    }

    api
      .get(apiInternalPriceRulePath({ price_list_id: priceListId, proposed_correction_id: proposedCorrection.id }))
      .then(({ data }) => {
        setPriceRule(data.price_rule);
        setPrice(data.price);
        if (resyncButtonClicked) {
          document.querySelector(`[name*="${objectName}[proposed_correction_price]"]`).value = data.price;
          document.querySelector(`[name*="${objectName}[proposed_correction_price]"]`).disabled = false;
        }
      });
  }, [priceListId, proposedCorrection, resyncButtonClicked]);

  const handleSelectionClear = () => {
    document.querySelector(`[name*="${objectName}[description]"]`).value = '';
    if (unitCostInput) unitCostInput.value = '';
    setProposedCorrection({ id: '', price: 0 });
  };

  useEffect(() => {
    input.current.dispatchEvent(new Event('change', { bubbles: true }));
  }, [price]);

  return (
    <>
      <input
        type="hidden"
        ref={input}
        name={correctionName}
        value={proposedCorrection?.id}
        data-price={price}
        data-js-proposal-proposed-correction
      />
      <input type="hidden" name={priceRuleName} value={priceRule?.id ?? ''} />
      {proposedCorrection?.id && (
        <div
          className="qmb-input --x-full"
          style={{ display: 'flex', padding: '8px', boxSizing: 'border-box', alignItems: 'center', width: '24rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <small>{proposedCorrection.type}</small>
            <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '.5rem', alignItems: 'baseline' }}>
              {proposedCorrection.name ?? proposedCorrection.description}
              <button type="button" className="qmb-control" style={{ padding: '3px 6px', borderRadius: '10px' }}>
                {/* <small>{priceRule?.asApplicable ?? 'No Price Rule'}</small> */}
              </button>
            </div>
          </div>
          <button type="button" className="qmb-control--icon" onClick={handleSelectionClear}>
            <i className="fa-light fa-xmark" />
          </button>
        </div>
      )}

      <SearchDropdown proposedCorrection={proposedCorrection} setProposedCorrection={setProposedCorrection} />
    </>
  );
}

MaterialsServicesSelector.propTypes = {
  proposalProposedCorrection: PropTypes.object.isRequired,
  proposedCorrection: PropTypes.object,
  correctionName: PropTypes.string.isRequired,
  priceRuleName: PropTypes.string.isRequired,
  objectName: PropTypes.string.isRequired
};

MaterialsServicesSelector.defaultProps = {
  proposedCorrection: {
    id: '',
    price: 0
  }
};

export default MaterialsServicesSelector;
